import { useMemo } from 'react';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedFraction from 'I18n/components/FormattedFraction';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Small } from 'foundations-components/transitional/components/Small';
import { IconToggle } from 'foundations-components/transitional/components/IconToggle';
import styled from 'styled-components';
import { STAR_RATING, STAR_RATING_FILLED, STAR_RATING_EMPTY_STATE, PILL_RATING, LabelSizesTemplate } from '../constants/RatingTypes';
import { PillIcon, StarRating, StarRatingFilled, StarRatingEmpty } from './AbstractRatingIcons';

// NOTE: using styled wrapper cause `Trellis` ones do not support aria-attributes
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RatingIconsWrapper = styled.div.withConfig({
  displayName: "Rating__RatingIconsWrapper",
  componentId: "vwep32-0"
})(["display:flex;"]);
export default function Rating({
  rating,
  label,
  iconSize = '600',
  justify,
  direction = 'row',
  labelSize = 'small',
  showTextRating,
  type = STAR_RATING_FILLED,
  use = 'default',
  'data-test-id': dataTestId
}) {
  const flexAlignment = direction === 'row' ? 'center' : 'start';
  const dataTestIdAttr = dataTestId ? `${dataTestId}-label` : undefined;
  const renderedTextRating = useMemo(() => {
    return use === 'card' ? /*#__PURE__*/_jsx(View, {
      marginInlineStart: "200",
      as: "span",
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx(FormattedFraction, {
          numerator: I18n.formatNumber(rating, {
            precision: 2
          }),
          denominator: 5
        })
      })
    }) : /*#__PURE__*/_jsx(View, {
      marginInlineStart: "200",
      as: "span",
      children: /*#__PURE__*/_jsx(FormattedFraction, {
        numerator: I18n.formatNumber(rating, {
          precision: 2
        }),
        denominator: 5
      })
    });
  }, [rating, use]);
  const IconComponent = useMemo(() => {
    switch (type) {
      case PILL_RATING:
        return PillIcon;
      case STAR_RATING:
        return StarRating;
      case STAR_RATING_FILLED:
        return StarRatingFilled;
      case STAR_RATING_EMPTY_STATE:
      default:
        return StarRatingEmpty;
    }
  }, [type]);
  const renderedIcons = useMemo(() => {
    return [...new Array(5)].map((__, i) => {
      const index = i + 1;
      const percentFilled = Math.min(100, Math.max(0, Math.round((rating - index + 1) * 100)));
      return /*#__PURE__*/_jsx(IconToggle, {
        index: index,
        checked: index <= rating,
        percentFilled: percentFilled,
        size: iconSize,
        IconComponent: IconComponent,
        disabled: true,
        readOnly: true,
        "aria-label": "",
        tabIndex: -1
      }, index);
    });
  }, [IconComponent, iconSize, rating]);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: direction,
    justifyContent: justify,
    alignItems: flexAlignment,
    rowGap: "100",
    columnGap: use === 'card' ? '200' : '300',
    children: [/*#__PURE__*/_jsxs(RatingIconsWrapper, {
      "aria-label": unescapedText('marketplace-ui-reviews.rating.overall'),
      "aria-valuemax": 5,
      "aria-valuemin": 0,
      "aria-valuenow": rating,
      "aria-valuetext": unescapedText('marketplace-ui-reviews.rating.accessibleLabel', {
        rating
      }),
      role: "meter",
      children: [renderedIcons, showTextRating && renderedTextRating]
    }), !!label && use === 'card' && /*#__PURE__*/_jsxs(Small, {
      use: "help",
      "data-test-id": dataTestIdAttr,
      children: ["(", label, ")"]
    }), !!label && use === 'default' && /*#__PURE__*/_jsx(Text, {
      as: "span",
      "data-test-id": dataTestIdAttr,
      typography: LabelSizesTemplate[labelSize],
      children: label
    })]
  });
}